import { t } from "@/lib/i18n"
import type { StatusTone } from "@/lib/status-colors"

/**
 * Shape, endpoints and copy lookup for staff-side item stock — dish-level
 * "existencias".
 *
 * ## Not the pantry
 *
 * `inventory.ts` tracks what the kitchen consumes (grams of jitomate). This
 * tracks what a manager may still sell of a *dish* — a count typed once at the
 * start of service and drawn down by orders, mirroring `Location::ItemStock`
 * on the server. The two features are unrelated except by shape: neither reads
 * the other's numbers, and a restaurant can run either, both, or neither.
 *
 * ## Both status flags arrive answered
 *
 * `depleted` and `low` are `Location::ItemStock#depleted?` / `#low?`, computed
 * server-side from a value re-read inside a row lock. Nothing here re-derives
 * a status by comparing `quantity` against `lowThreshold` itself — the same
 * posture `inventory.ts`'s `supplyAlert` takes with `short`/`belowReorderPoint`,
 * and for the same reason: a second copy of that comparison in the browser is
 * a copy that agrees with the server only on the day it is written.
 *
 * ## The not-granted state is never rendered here
 *
 * `Staff::Location::FeatureGuard` turns the URL away before this screen's
 * props ever exist. There is no "feature off" card to build.
 */

export type ItemStockRow = {
  id: string
  itemId: string
  itemName: string
  itemEmoji: string | null
  quantity: number
  lowThreshold: number
  depleted: boolean
  low: boolean
}

/** An active item with no stock row yet — offered by the create form. */
export type CountableItemOption = {
  value: string
  label: string
}

/** Rails validation errors: one field key to one-or-many messages. */
export type ItemStockErrors = Record<string, string[]>

// Underscored, matching the route `resources :item_stocks` produces. The page
// directory and the Inertia component name are hyphenated -- that split is the
// house convention, not an oversight: `/manage/central_items` renders
// `staff/location/manage/central-items/index` the same way.
export const ITEM_STOCK_PATHS = {
  itemStocks: "/manage/item_stocks",
  itemStock: (id: string) => `/manage/item_stocks/${id}`,
} as const

const I18N_PREFIX = "ui.react.features.staff.item_stocks"

export function tItemStock(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * A row's status, mirroring `Location::ItemStock#depleted?` / `#low?`.
 *
 * `depleted` outranks `low` because a single write can cross both lines at
 * once (10 -> 0 with a threshold of 3), and "se acabó" is the sentence that
 * matters then — the same edge-triggered priority `emit_crossing` gives the
 * two events on the server.
 */
export type ItemStockStatus = "depleted" | "low" | "available"

export function itemStockStatus(row: Pick<ItemStockRow, "depleted" | "low">): ItemStockStatus {
  if (row.depleted) return "depleted"
  if (row.low) return "low"
  return "available"
}

/**
 * The dot rule: every status resolves to exactly one semantic status token,
 * never a raw Tailwind hue. `depleted` maps to `disabled` (grey) rather than
 * a danger red — a dish that stopped selling is an absence, not an alarm; the
 * warning is reserved for `low`, the state a manager can still act on.
 */
export function itemStockTone(status: ItemStockStatus): StatusTone {
  switch (status) {
    case "depleted":
      return "disabled"
    case "low":
      return "warning"
    case "available":
      return "available"
  }
}
